import { FIREBASE_STORAGE_KEYS } from '../../constants/authConstants';

/**
 * Checks if the user account is locked
 * @returns {Object|null} - Lock information or null if not locked
 */
export const checkAccountLock = () => {
  const lockUntil = localStorage.getItem(FIREBASE_STORAGE_KEYS.LOCK_UNTIL);
  
  if (!lockUntil) {
    return null;
  }

  const lockUntilDate = new Date(lockUntil);
  const now = new Date();

  if (lockUntilDate > now) {
    return {
      isLocked: true,
      lockUntilDate,
      remainingTime: lockUntilDate - now,
    };
  }

  // Lock expired, remove it
  localStorage.removeItem(FIREBASE_STORAGE_KEYS.LOCK_UNTIL);
  return null;
};

/**
 * Sets account lock until a specific date
 * @param {Date|string} lockUntilDate - Date until which the account is locked
 */
export const setAccountLock = (lockUntilDate) => {
  const dateToStore = lockUntilDate instanceof Date 
    ? lockUntilDate.toISOString() 
    : lockUntilDate;
  
  localStorage.setItem(FIREBASE_STORAGE_KEYS.LOCK_UNTIL, dateToStore);
};

/**
 * Removes account lock
 */
export const clearAccountLock = () => {
  localStorage.removeItem(FIREBASE_STORAGE_KEYS.LOCK_UNTIL);
};

/**
 * Gets the remaining lock time in minutes
 * @returns {number} - Remaining time in minutes, or 0 if not locked
 */
export const getRemainingLockTimeInMinutes = () => {
  const lockInfo = checkAccountLock();
  
  if (!lockInfo || !lockInfo.isLocked) {
    return 0;
  }

  return Math.ceil(lockInfo.remainingTime / (1000 * 60));
};

/**
 * Checks if account is currently locked and returns formatted info
 * @param {Function} setLockUntil - Optional setter function to update lock state
 * @returns {Object} - Lock status and information
 */
export const checkIfAccountIsLocked = (setLockUntil = null) => {
  const lockInfo = checkAccountLock();

  if (lockInfo && lockInfo.isLocked) {
    if (setLockUntil) {
      setLockUntil(lockInfo.lockUntilDate);
    }
    return {
      isLocked: true,
      lockUntilDate: lockInfo.lockUntilDate,
      remainingMinutes: getRemainingLockTimeInMinutes(),
    };
  }

  if (setLockUntil) {
    setLockUntil(null);
  }

  return {
    isLocked: false,
    lockUntilDate: null,
    remainingMinutes: 0,
  };
};

/**
 * Initializes lock check with state setter
 * @param {Function} setLockUntil - State setter function for lock date
 */
export const initializeLockCheck = (setLockUntil) => {
  return checkIfAccountIsLocked(setLockUntil);
};

